import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Pill,
  Clock,
  TrendingUp,
  CalendarCheck,
  ShieldCheck,
  Phone,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import QuizPaymentStep, { QuizOrderReceived } from "./QuizPaymentStep"; // QuizPaymentStep retained for backward compat (no longer wired into the funnel — payments are handled manually by admins via "Save Card on File")
// eslint-disable-next-line no-unused-vars
const _QuizPaymentStep = QuizPaymentStep;
import { formatUSPhone } from "@/lib/formatters";
import useLeadCapture from "@/hooks/useLeadCapture";
import { trackLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── PEPTIDE DATABASE ───────────────────────────────────────
const peptideDB = {
  semaglutide: {
    name: "Semaglutide",
    category: "Weight Management",
    description:
      "A GLP-1 receptor agonist that regulates appetite and blood sugar. Clinically proven to reduce body weight by 15-20% when combined with lifestyle changes.",
    results: "Significant reduction in cravings, steady fat loss, improved metabolic markers, better blood sugar control.",
    timeline: "Noticeable appetite changes in 1-2 weeks. Visible weight loss within 4-8 weeks. Full results at 3-6 months.",
  },
  tirzepatide: {
    name: "Tirzepatide",
    category: "Weight Management",
    description:
      "A dual GIP/GLP-1 receptor agonist — the most advanced weight loss peptide available. Works on two hormonal pathways simultaneously for enhanced results.",
    results: "Accelerated fat loss, improved insulin sensitivity, reduced visceral fat, sustained energy throughout the day.",
    timeline: "Appetite suppression within days. Measurable weight loss in 4 weeks. Transformative results at 3-6 months.",
  },
  bpc157: {
    name: "BPC-157",
    category: "Recovery & Healing",
    description:
      "A body-protecting compound derived from gastric juice. Accelerates healing of muscles, tendons, ligaments, and gut lining with powerful anti-inflammatory effects.",
    results: "Faster tissue repair, reduced joint pain, improved gut health, decreased inflammation throughout the body.",
    timeline: "Pain relief often within 1-2 weeks. Significant healing at 4-6 weeks. Full tissue repair at 8-12 weeks.",
  },
  tb500: {
    name: "TB-500 (Thymosin Beta-4)",
    category: "Recovery & Performance",
    description:
      "A naturally occurring peptide that promotes new blood vessel growth, muscle fiber repair, and cell migration to injury sites.",
    results: "Enhanced muscle recovery, reduced stiffness, improved flexibility, accelerated wound healing.",
    timeline: "Reduced soreness in 1-2 weeks. Notable recovery improvements at 4 weeks. Peak benefits at 8-12 weeks.",
  },
  ghkcu: {
    name: "GHK-Cu",
    category: "Anti-Aging & Skin",
    description:
      "A copper peptide that stimulates collagen synthesis, tightens skin, reduces fine lines, and promotes hair growth. The gold standard in regenerative skincare.",
    results: "Firmer skin, reduced wrinkles, improved skin texture, thicker hair, faster wound healing.",
    timeline: "Skin texture changes in 2-4 weeks. Visible anti-aging results at 6-8 weeks. Cumulative improvements over 3-6 months.",
  },
  epithalon: {
    name: "Epithalon",
    category: "Longevity",
    description:
      "A telomerase-activating peptide that lengthens telomeres — the biological clock of your cells. One of the most researched longevity peptides in the world.",
    results: "Improved sleep quality, enhanced immune function, slowed biological aging markers, increased cellular vitality.",
    timeline: "Better sleep within 1-2 weeks. Energy improvements at 4 weeks. Measurable telomere changes over 3-6 months.",
  },
  cjc1295: {
    name: "CJC-1295 / Ipamorelin",
    category: "Growth Hormone & Performance",
    description:
      "A growth hormone releasing peptide stack that naturally boosts your GH levels. Improves body composition, sleep, recovery, and overall vitality without the risks of synthetic HGH.",
    results: "Better sleep, increased lean muscle, reduced body fat, faster recovery, improved skin elasticity.",
    timeline: "Sleep improvements in 1-2 weeks. Body composition changes at 4-8 weeks. Full optimization at 3-6 months.",
  },
  thymosin: {
    name: "Thymosin Alpha-1",
    category: "Immune Defense",
    description:
      "A powerful immune-modulating peptide used worldwide to strengthen the body's natural defense system. Enhances T-cell function and reduces chronic inflammation.",
    results: "Stronger immune response, fewer infections, reduced autoimmune flare-ups, better overall resilience.",
    timeline: "Immune support begins within days. Measurable immune markers improve at 4 weeks. Full benefits at 2-3 months.",
  },
  semax: {
    name: "Semax",
    category: "Cognitive Enhancement",
    description:
      "A neuropeptide that boosts BDNF (brain-derived neurotrophic factor), enhancing focus, memory, and mental clarity while reducing anxiety.",
    results: "Sharper focus, improved working memory, reduced brain fog, lower anxiety, enhanced motivation.",
    timeline: "Mental clarity improvements within days. Significant cognitive gains at 2-4 weeks. Peak performance at 6-8 weeks.",
  },
  pt141: {
    name: "PT-141 (Bremelanotide)",
    category: "Sexual Wellness",
    description:
      "Works directly on the central nervous system to enhance sexual desire and performance in both men and women. Unlike other treatments, it targets the root cause — your brain chemistry.",
    results: "Increased natural desire, improved arousal, enhanced performance, restored confidence.",
    timeline: "Effects noticeable within 1-2 hours of administration. Consistent improvements with regular protocol over 4-8 weeks.",
  },
};

// Map goals/frustrations to peptide recommendations
function getRecommendedPeptides(answers) {
  const goals = answers.goal || [];
  const frustrations = answers.frustration || [];
  const condition = answers.condition || [];
  const recs = new Set();

  // Goal-based mapping
  if (goals.includes("goal_weightloss")) {
    recs.add("semaglutide");
    recs.add("tirzepatide");
  }
  if (goals.includes("goal_energy")) {
    recs.add("cjc1295");
    recs.add("bpc157");
  }
  if (goals.includes("goal_antiaging")) {
    recs.add("ghkcu");
    recs.add("epithalon");
  }
  if (goals.includes("goal_muscle")) {
    recs.add("cjc1295");
    recs.add("tb500");
  }
  if (goals.includes("goal_health")) {
    recs.add("thymosin");
    recs.add("bpc157");
  }

  // Frustration-based additions
  if (frustrations.includes("cant_lose_weight") && !recs.has("semaglutide")) {
    recs.add("semaglutide");
  }
  if (frustrations.includes("low_energy")) {
    recs.add("cjc1295");
  }
  if (frustrations.includes("no_results")) {
    recs.add("tb500");
    recs.add("cjc1295");
  }
  if (frustrations.includes("aging_fast")) {
    recs.add("ghkcu");
    recs.add("epithalon");
  }
  if (frustrations.includes("plateaued")) {
    recs.add("cjc1295");
  }

  // Condition-based additions
  if (condition.includes("overweight") || condition.includes("slightly_overweight")) {
    recs.add("semaglutide");
  }
  if (condition.includes("athletic") || condition.includes("fit_optimize")) {
    recs.add("cjc1295");
    recs.add("semax");
  }

  // Always include at least 2
  if (recs.size < 2) {
    recs.add("bpc157");
    recs.add("cjc1295");
  }

  return Array.from(recs)
    .slice(0, 4)
    .map((key) => peptideDB[key]);
}

// ─── QUIZ STEPS ─────────────────────────────────────────────
const steps = [
  {
    id: "goal",
    overline: "Step 1 of 9",
    question: "What is your main goal right now?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "goal_weightloss", label: "Lose stubborn body fat" },
      { value: "goal_energy", label: "Improve energy & recovery" },
      { value: "goal_antiaging", label: "Anti-aging & longevity" },
      { value: "goal_muscle", label: "Build lean muscle" },
      { value: "goal_health", label: "Improve overall health" },
    ],
  },
  {
    id: "frustration",
    overline: "Step 2 of 9",
    question: "What's been your biggest challenge?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "cant_lose_weight", label: "I can't lose weight no matter what I try" },
      { value: "low_energy", label: "I feel tired / low energy" },
      { value: "no_results", label: "I'm not seeing results from workouts" },
      { value: "aging_fast", label: "I feel like I'm aging faster than I should" },
      { value: "plateaued", label: "I've plateaued" },
    ],
  },
  {
    id: "age_range",
    overline: "Step 3 of 9",
    question: "What's your age range?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "under_30", label: "Under 30" },
      { value: "30_39", label: "30 – 39" },
      { value: "40_49", label: "40 – 49" },
      { value: "50_59", label: "50 – 59" },
      { value: "60_plus", label: "60+" },
    ],
  },
  {
    id: "condition",
    overline: "Step 4 of 9",
    question: "How would you describe your current shape/health?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "overweight", label: "Overweight" },
      { value: "slightly_overweight", label: "Slightly overweight" },
      { value: "average", label: "Average" },
      { value: "fit_optimize", label: "Fit but want optimization" },
      { value: "athletic", label: "Athletic" },
    ],
  },
  {
    id: "experience",
    overline: "Step 5 of 9",
    question: "Have you tried anything for this before?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "diet_exercise", label: "Diet & exercise" },
      { value: "supplements", label: "Supplements" },
      { value: "hormone_therapy", label: "Hormone therapy (TRT/HRT)" },
      { value: "peptides_before", label: "Peptides before" },
      { value: "nothing_yet", label: "Nothing yet" },
    ],
  },
  {
    id: "timeframe",
    overline: "Step 6 of 9",
    question: "How soon are you looking to see results?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "asap", label: "ASAP" },
      { value: "1_3_months", label: "Within 1–3 months" },
      { value: "3_6_months", label: "3–6 months" },
      { value: "exploring", label: "Just exploring" },
    ],
  },
  {
    id: "commitment",
    overline: "Step 7 of 9",
    question: "How serious are you about improving your results?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "very_serious", label: "Very serious — ready to start" },
      { value: "serious_learn", label: "Serious but want to learn more" },
      { value: "researching", label: "Just researching" },
    ],
  },
  {
    id: "medical",
    overline: "Step 8 of 9",
    question: "Are you open to a doctor-guided program if you qualify?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { value: "yes", label: "Yes" },
      { value: "maybe", label: "Maybe" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: "budget",
    overline: "Step 9 of 9",
    question: "Most personalized peptide programs range from $200–$800+/month.",
    subtitle: "Are you comfortable in that range if it fits your goals?",
    type: "multi",
    options: [
      { value: "yes", label: "Yes" },
      { value: "maybe", label: "Maybe" },
      { value: "no", label: "No" },
    ],
  },
];

// ─── COMPONENTS ─────────────────────────────────────────────
function QuizProgress({ current, total }) {
  const pct = ((current + 1) / total) * 100;
  return (
    <div className="w-full mb-10">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-sans font-medium text-[#2A3324]/40 uppercase tracking-wider">
          Progress
        </span>
        <span className="text-xs font-sans font-medium text-[#5C715E]">
          {current + 1} / {total}
        </span>
      </div>
      <div className="w-full h-1.5 bg-[#2A3324]/10 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-[#5C715E] rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function OptionButton({ label, selected, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full text-left rounded-2xl p-5 md:p-6 border transition-all duration-300 ${
        selected
          ? "bg-[#2A3324] border-[#2A3324] text-[#F7F5F0]"
          : "bg-[#F7F5F0] border-[#2A3324]/10 text-[#2A3324] hover:border-[#2A3324]/30 hover:bg-[#F7F5F0]/80"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-base md:text-lg font-sans font-medium">{label}</span>
        {selected && (
          <CheckCircle2 size={20} strokeWidth={1.5} className="text-[#C47C5D] shrink-0" />
        )}
      </div>
    </button>
  );
}

// ─── REPORT PAGE ────────────────────────────────────────────
function PeptideReport({ peptides, contactInfo, setContactInfo, onSubmit, loading }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Report header */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-[#5C715E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldCheck size={32} strokeWidth={1.5} className="text-[#5C715E]" />
        </div>
        <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-3 block">
          Your Personalized Protocol
        </span>
        <h2
          data-testid="report-heading"
          className="text-3xl md:text-4xl font-serif font-light tracking-tight text-[#2A3324] mb-3"
        >
          Your Recommended Peptides
        </h2>
        <p className="text-base font-sans font-light text-[#2A3324]/60 max-w-lg mx-auto">
          Based on your answers, here's the personalized peptide protocol our physicians would
          likely recommend for you.
        </p>
      </div>

      {/* Claim Section with Contact Fields — MOVED TO TOP */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-[#F7F5F0] rounded-[1.5rem] p-6 md:p-8 border border-[#2A3324]/5 mb-12"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#5C715E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarCheck size={24} strokeWidth={1.5} className="text-[#5C715E]" />
          </div>
          <h3 className="text-2xl md:text-3xl font-serif font-normal tracking-tight text-[#2A3324] mb-2">
            Claim Your Protocol — Free Doctor Call
          </h3>
          <p className="text-sm font-sans font-light text-[#2A3324]/50 max-w-md mx-auto">
            Enter your info to schedule a free doctor discussion and lock in your personalized plan
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg mx-auto mb-6">
          <div>
            <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1.5 block">
              First Name *
            </label>
            <input
              data-testid="quiz-first-name"
              type="text"
              value={contactInfo.first_name}
              onChange={(e) => setContactInfo({ ...contactInfo, first_name: e.target.value })}
              placeholder="First name"
              className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-sm font-sans text-[#2A3324] focus:outline-none focus:ring-2 focus:ring-[#5C715E]/20 placeholder-[#2A3324]/30"
            />
          </div>
          <div>
            <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1.5 block">
              Last Name *
            </label>
            <input
              data-testid="quiz-last-name"
              type="text"
              value={contactInfo.last_name}
              onChange={(e) => setContactInfo({ ...contactInfo, last_name: e.target.value })}
              placeholder="Last name"
              className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-sm font-sans text-[#2A3324] focus:outline-none focus:ring-2 focus:ring-[#5C715E]/20 placeholder-[#2A3324]/30"
            />
          </div>
          <div>
            <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1.5 block">
              Email *
            </label>
            <input
              data-testid="quiz-email"
              type="email"
              value={contactInfo.email}
              onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-sm font-sans text-[#2A3324] focus:outline-none focus:ring-2 focus:ring-[#5C715E]/20 placeholder-[#2A3324]/30"
            />
          </div>
          <div>
            <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1.5 block">
              Phone *
            </label>
            <input
              data-testid="quiz-phone"
              type="tel"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo({ ...contactInfo, phone: formatUSPhone(e.target.value) })}
              placeholder="(555) 000-0000"
              maxLength={17}
              className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-sm font-sans text-[#2A3324] focus:outline-none focus:ring-2 focus:ring-[#5C715E]/20 placeholder-[#2A3324]/30"
            />
          </div>
        </div>

        <div className="text-center">
          <button
            data-testid="quiz-submit-btn"
            onClick={onSubmit}
            disabled={!contactInfo.first_name || !contactInfo.last_name || !contactInfo.email || !contactInfo.phone || loading}
            className={`rounded-full px-10 py-4 text-base font-sans font-medium tracking-wide transition-all duration-300 hover:-translate-y-1 shadow-sm inline-flex items-center gap-3 ${
              contactInfo.first_name && contactInfo.last_name && contactInfo.email && contactInfo.phone
                ? "bg-[#5C715E] text-[#F7F5F0] hover:bg-[#4A5A4B]"
                : "bg-[#2A3324]/10 text-[#2A3324]/30 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Scheduling...
              </>
            ) : (
              <>
                <CalendarCheck size={18} strokeWidth={1.5} />
                Claim Your Peptides — Schedule Free Dr. Call
                <ArrowRight size={18} strokeWidth={1.5} />
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Peptide cards */}
      <div className="space-y-6 mb-12">
        {peptides.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            data-testid={`report-peptide-${i}`}
            className="bg-[#F7F5F0] rounded-[1.5rem] p-6 md:p-8 border border-[#2A3324]/5"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="text-xs font-sans font-medium uppercase tracking-[0.15em] text-[#C47C5D] block mb-1">
                  {p.category}
                </span>
                <h3 className="text-2xl md:text-3xl font-serif font-normal tracking-tight text-[#2A3324]">
                  {p.name}
                </h3>
              </div>
              <Pill size={24} strokeWidth={1.5} className="text-[#5C715E] shrink-0 mt-1" />
            </div>
            <p className="text-base font-sans font-light leading-relaxed text-[#2A3324]/70 mb-6">
              {p.description}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 border border-[#2A3324]/5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={16} strokeWidth={1.5} className="text-[#5C715E]" />
                  <span className="text-xs font-sans font-semibold uppercase tracking-wider text-[#5C715E]">
                    Expected Results
                  </span>
                </div>
                <p className="text-sm font-sans font-light leading-relaxed text-[#2A3324]/70">
                  {p.results}
                </p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-[#2A3324]/5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} strokeWidth={1.5} className="text-[#C47C5D]" />
                  <span className="text-xs font-sans font-semibold uppercase tracking-wider text-[#C47C5D]">
                    Timeline
                  </span>
                </div>
                <p className="text-sm font-sans font-light leading-relaxed text-[#2A3324]/70">
                  {p.timeline}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Disclaimer */}
      <div className="bg-[#EFECE5] rounded-xl p-5 mb-10 border border-[#2A3324]/5">
        <p className="text-xs font-sans font-light text-[#2A3324]/50 leading-relaxed">
          <span className="font-medium text-[#2A3324]/70">Important:</span> This is a preliminary
          recommendation based on your quiz responses. Your actual protocol will be customized by a
          licensed physician after a thorough medical evaluation and lab work review.
        </p>
      </div>
    </motion.div>
  );
}

// ─── MAIN QUIZ ──────────────────────────────────────────────
export default function QuizSection() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contactInfo, setContactInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("quiz"); // quiz | report | checkout | order_received | done(legacy)
  const [direction, setDirection] = useState(1);
  const [checkoutResult, setCheckoutResult] = useState(null);  // payload returned by /quiz-checkout

  const step = steps[currentStep];

  const recommendedPeptides = useMemo(
    () => getRecommendedPeptides(answers),
    [answers]
  );

  const handleMultiSelect = (value) => {
    const current = answers[step.id] || [];
    if (current.includes(value)) {
      setAnswers({ ...answers, [step.id]: current.filter((v) => v !== value) });
    } else {
      setAnswers({ ...answers, [step.id]: [...current, value] });
    }
  };

  const canProceed = () => {
    if (step.type === "multi") return (answers[step.id] || []).length > 0;
    return false;
  };

  const nextStep = () => {
    if (!canProceed()) {
      toast.error("Please make a selection before continuing");
      return;
    }
    if (currentStep < steps.length - 1) {
      setDirection(1);
      setCurrentStep(currentStep + 1);
    } else {
      // Last quiz step → show report
      setPhase("report");
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const submitQuiz = async () => {
    if (!contactInfo.first_name || !contactInfo.last_name || !contactInfo.email || !contactInfo.phone) {
      toast.error("Please fill in your name, email, and phone — these are required.");
      return;
    }
    setLoading(true);
    try {
      // Submit lead — backend creates the applicant in 'New Applicant' status
      // and routes to the right doctor/nurse for follow-up. No payment required
      // here; admins use the manual "Save Card on File" button later when
      // they're ready to bill the client.
      await axios.post(`${API}/quiz`, {
        answers,
        contact: { ...contactInfo },
        recommended_peptides: recommendedPeptides.map((p) => p.name),
      });
      trackLead({
        content_name: "Quiz",
        form_id: "quiz",
        email: contactInfo.email,
        phone: contactInfo.phone,
      });
    } catch {
      // Don't block the funnel if the analytics POST fails — show thank-you anyway.
    } finally {
      setLoading(false);
      // Always advance to the "we'll be in touch" thank-you page.
      setPhase("done");
    }
  };

  const getQualificationLevel = () => {
    let score = 0;
    const commitment = answers.commitment || [];
    const medical = answers.medical || [];
    const budget = answers.budget || [];
    const timeframe = answers.timeframe || [];
    if (commitment.includes("very_serious") || commitment.includes("serious_learn")) score += 3;
    if (medical.includes("yes")) score += 3;
    if (budget.includes("yes")) score += 3;
    if (timeframe.includes("asap") || timeframe.includes("1_3_months")) score += 2;
    if (commitment.includes("very_serious")) score += 1;
    if (score >= 9) return "strong";
    if (score >= 5) return "good";
    return "potential";
  };

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };

  // ─── PHASE: ORDER RECEIVED (legacy — still used if anything sets this phase) ──
  if (phase === "order_received") {
    return (
      <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32 bg-[#EFECE5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <QuizOrderReceived result={checkoutResult} />
        </div>
      </section>
    );
  }

  // ─── PHASE: DONE (legacy fallback — kept only as a safety net if the
  //   patient explicitly bails out of the payment step. Routes them to the
  //   text-signup invite path so we still capture the lead.) ──
  if (phase === "done") {
    const level = getQualificationLevel();
    return (
      <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32 bg-[#EFECE5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-[2rem] p-8 md:p-14 border border-[#2A3324]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center"
          >
            <div className="w-20 h-20 bg-[#5C715E]/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Phone size={36} strokeWidth={1.5} className="text-[#5C715E]" />
            </div>
            <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-4 block">
              {level === "strong" && "Excellent Match"}
              {level === "good" && "Great Candidate"}
              {level === "potential" && "Potential Candidate"}
            </span>
            <h2
              data-testid="quiz-done-heading"
              className="text-3xl md:text-4xl lg:text-5xl font-serif font-light tracking-tight text-[#2A3324] mb-4"
            >
              We saved your protocol
            </h2>
            <p className="text-base md:text-lg font-sans font-light leading-relaxed text-[#2A3324]/60 max-w-xl mx-auto mb-4">
              Whenever you're ready to start, just text us at the number we'll send to {contactInfo.phone}.
              No pressure — your spot is held.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  // ─── PHASE: REPORT ────────────────────────────────────────
  if (phase === "report") {
    return (
      <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32 bg-[#EFECE5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-[2rem] p-8 md:p-12 border border-[#2A3324]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <PeptideReport
              peptides={recommendedPeptides}
              contactInfo={contactInfo}
              setContactInfo={setContactInfo}
              onSubmit={submitQuiz}
              loading={loading}
            />
          </div>
        </div>
      </section>
    );
  }

  // ─── PHASE: QUIZ ──────────────────────────────────────────
  return (
    <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32 bg-[#EFECE5]">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-4 block">
            Personalized Protocol
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-[#2A3324]">
            Find Your Personalized Peptide Protocol
          </h2>
          <p className="mt-4 text-base md:text-lg font-sans font-light text-[#2A3324]/60 max-w-2xl mx-auto">
            Answer a few quick questions to see if you qualify for a doctor-guided peptide program
          </p>
        </motion.div>

        <div className="bg-white rounded-[2rem] p-8 md:p-12 border border-[#2A3324]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <QuizProgress current={currentStep} total={steps.length} />

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mb-8">
                <span className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-2 block">
                  {step.overline}
                </span>
                <h3 className="text-2xl md:text-3xl font-serif font-normal tracking-tight text-[#2A3324] mb-2">
                  {step.question}
                </h3>
                <p className="text-sm md:text-base font-sans font-light text-[#2A3324]/50">
                  {step.subtitle}
                </p>
              </div>

              {step.type === "multi" && (
                <div className="space-y-3">
                  {step.options.map((opt) => (
                    <OptionButton
                      key={opt.value}
                      testId={`quiz-option-${opt.value}`}
                      label={opt.label}
                      selected={(answers[step.id] || []).includes(opt.value)}
                      onClick={() => handleMultiSelect(opt.value)}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-10 pt-8 border-t border-[#2A3324]/10">
            <button
              data-testid="quiz-back-btn"
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`rounded-full px-6 py-3 text-sm font-sans font-medium transition-all duration-300 inline-flex items-center gap-2 ${
                currentStep === 0
                  ? "text-[#2A3324]/20 cursor-not-allowed"
                  : "text-[#2A3324] hover:bg-[#2A3324]/5 border border-[#2A3324]/20"
              }`}
            >
              <ArrowLeft size={16} strokeWidth={1.5} />
              Back
            </button>

            <button
              data-testid="quiz-next-btn"
              onClick={nextStep}
              disabled={!canProceed()}
              className={`rounded-full px-8 py-3 text-sm font-sans font-medium transition-all duration-300 inline-flex items-center gap-2 ${
                canProceed()
                  ? "bg-[#5C715E] text-[#F7F5F0] hover:bg-[#4A5A4B] hover:-translate-y-0.5 shadow-sm"
                  : "bg-[#2A3324]/10 text-[#2A3324]/30 cursor-not-allowed"
              }`}
            >
              {currentStep === steps.length - 1 ? "See My Protocol" : "Continue"}
              <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
